import { useEffect, useState } from 'react'
import { flushSync } from 'react-dom'
import { Button } from '@fluentui/react-components'
import {
  ArrowRight20Regular,
  Dismiss20Regular,
  Navigation20Regular,
  WeatherMoon20Regular,
  WeatherSunny20Regular,
} from '@fluentui/react-icons'
import { navigation } from '../lib/site'
import { usePrefersReducedMotion } from '../lib/hooks'
import type { Route } from '../lib/router'
import { BrandLockup } from './BrandMark'

type ViewTransitionDocument = Document & {
  startViewTransition?: (callback: () => void) => { finished: Promise<void> }
}

type Props = {
  path: Route
  dark: boolean
  setDark: (next: boolean) => void
}

export function SiteHeader({ path, dark, setDark }: Props) {
  const [menuOpen, setMenuOpen] = useState(false)
  const reduced = usePrefersReducedMotion()

  useEffect(() => setMenuOpen(false), [path])

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [menuOpen])

  useEffect(() => {
    if (!menuOpen) return
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setMenuOpen(false)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [menuOpen])

  /** Circular wipe from the toggle itself, where the API is available. */
  const toggleTheme = (event: React.MouseEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const root = document.documentElement
    root.style.setProperty('--wipe-x', `${rect.left + rect.width / 2}px`)
    root.style.setProperty('--wipe-y', `${rect.top + rect.height / 2}px`)

    const doc = document as ViewTransitionDocument
    if (reduced || typeof doc.startViewTransition !== 'function') {
      setDark(!dark)
      return
    }

    doc.startViewTransition(() => flushSync(() => setDark(!dark)))
  }

  return (
    <header className={menuOpen ? 'site-header is-open' : 'site-header'}>
      <div className="header-inner shell-wide">
        <a className="brand" href="/" aria-label="Libris home">
          <BrandLockup height={46} />
        </a>

        <nav className="desktop-nav" aria-label="Primary">
          {navigation.map(item => (
            <a key={item.href} href={item.href} className={path === item.href ? 'is-active' : undefined}>
              <span>{item.label}</span>
            </a>
          ))}
        </nav>

        <div className="header-actions">
          <button
            type="button"
            className="icon-button"
            onClick={toggleTheme}
            aria-pressed={dark}
            aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'}
            title={dark ? 'Light theme' : 'Dark theme'}
          >
            {dark ? <WeatherSunny20Regular /> : <WeatherMoon20Regular />}
          </button>

          <Button
            className="header-cta"
            appearance="primary"
            as="a"
            href="/#contact"
            icon={<ArrowRight20Regular />}
            iconPosition="after"
          >
            Plan a deployment
          </Button>

          <button
            type="button"
            className="icon-button menu-toggle"
            onClick={() => setMenuOpen(open => !open)}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            {menuOpen ? <Dismiss20Regular /> : <Navigation20Regular />}
          </button>
        </div>
      </div>

      <div className="scroll-rail" aria-hidden="true">
        <i />
      </div>

      <div id="mobile-nav" className="mobile-nav" hidden={!menuOpen}>
        <nav aria-label="Primary mobile">
          {navigation.map((item, index) => (
            <a key={item.href} href={item.href} style={{ ['--i' as string]: index }}>
              <span className="mono">0{index + 1}</span>
              <b>{item.label}</b>
              <small>{item.summary}</small>
            </a>
          ))}
        </nav>
        <Button
          appearance="primary"
          size="large"
          as="a"
          href="/#contact"
          icon={<ArrowRight20Regular />}
          iconPosition="after"
        >
          Plan a deployment
        </Button>
      </div>
    </header>
  )
}
